// pages/OnboardingSettings.tsx
import { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Grid,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import useAuth from 'hooks/useAuth'; // Assuming you have a hook to get user data
import useCustomTranslation from 'hooks/useCustomTranslation';

const OnboardingSettings = () => {
  const { getToken, user } = useAuth(); // Get user data from auth context
  const { t } = useCustomTranslation();
  const [data, setData] = useState([]);
  const [values, setValues] = useState([]);
  const [company, setCompany] = useState([]);
  const [companyId, setCompanyId] = useState(null); // State to store company ID

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().max(255).required('First name is required'),
    last_name: Yup.string().max(255).required('Last name is required'),
    jobTitle: Yup.string().max(255),
    companySearch: Yup.string().max(255),
    companyName: Yup.string().max(255).required('Company name is required'),
    companyEmailDomain: Yup.string().max(255),
    companySize: Yup.string().max(255),
    industry: Yup.string().max(255),
  });

  const initialValues = {
    first_name: data.first_name || '',
    last_name: data.last_name || '',
    jobTitle: data.job_title || '',
    companySearch: '',
    companyId: data.company_id || '',
    companyName: company.name || '',
    companyEmailDomain: company.email_domain || '',
    companySize: company.company_size || '',
    industry: company.industry || '',
  };

  const getInitialValues = (data, company) => {
    const newUser = {
      first_name: '',
      last_name: '',
      jobTitle: '',
      companySearch: '',
      companyId: '',
      companyName: '',
      companyEmailDomain: '',
      companySize: '',
      industry: '',
    };

    if (data || company) {
      // Merges the newUser with the data and company if provided
      return Object.assign({}, newUser, {
        first_name: data?.first_name || '',
        last_name: data?.last_name || '',
        jobTitle: data?.job_title || '',
        companyId: data?.company_id || company?.id || '',
        companyName: company?.name || '',
        companyEmailDomain: company?.email_domain || '',
        companySize: company?.company_size || '',
        industry: company?.industry || '',
      });
    }

    return newUser;
  };


  async function fetchUser() {
    try {
      let url = `${process.env.REACT_APP_URL}/api/users/user-data/`;
      const token = await getToken();

      const headers = new Headers();

      headers.append('Content-Type', 'application/json');
      headers.append('Authorization', `Bearer ${token}`);

      const response = await fetch(url, {
        method: 'GET',
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setData(data);
        if (data.company_id) {
          setCompanyId(data.company_id);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  // useEffect(() => {
  //   if (data) {
  //     console.log(initialValues)
  //   }
  // }, [data]);

  async function fetchCompany() {
    try {
      let url = `${process.env.REACT_APP_URL}/api/users/company/`;
      const token = await getToken();

      const headers = new Headers();

      headers.append('Content-Type', 'application/json');
      headers.append('Authorization', `Bearer ${token}`);

      const response = await fetch(url, {
        method: 'GET',
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setCompany(data);
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function postUser(data) {
    try {
      let url = `${process.env.REACT_APP_URL}/api/users/user-data/`;
      const token = await getToken();

      const headers = new Headers();

      headers.append('Content-Type', 'application/json');
      headers.append('Authorization', `Bearer ${token}`);

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (response.ok) {
        const data = await response.json();
        console.log(data);
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    fetchUser();
    if (companyId) {
      fetchCompany();  // This runs if companyId is available
    }
  }, [companyId]);

  useEffect(() => {
    console.log(data)
    const dataReady = data && Object.keys(data).length > 0;
    const companyReady = company && Object.keys(company).length > 0;

    if (dataReady || companyReady) {
      const initialValues = getInitialValues(data, company);
      console.log(initialValues);
      setValues(initialValues);
    }
  }, [data, company]);

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ padding: 4, marginTop: 6 }}>
        <Typography variant="h5" gutterBottom>
          {t('Account settings')}
        </Typography>

        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            console.log(values);
            // TODO: submit to API
            postUser(values);
          }}
        >
          {({ getFieldProps, values, errors, touched, handleChange, handleBlur }) => (
            < Form noValidate>
              <Typography variant="h6" sx={{ mt: 3 }}>
                Your Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="first_name"
                    label="First Name"
                    // value={values.first_name}
                    {...getFieldProps('first_name')}
                    // onChange={() => {
                    //   console.log(values)
                    //   handleChange();
                    // }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.first_name && Boolean(errors.first_name)}
                    helperText={touched.first_name && errors.first_name}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="last_name"
                    label="Last Name"
                    // value={values.last_name}
                    {...getFieldProps('last_name')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.last_name && Boolean(errors.last_name)}
                    helperText={touched.last_name && errors.last_name}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="jobTitle"
                    label="Job Title or Role (optional)"
                    // value={values.jobTitle}
                    {...getFieldProps('jobTitle')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
                {data && data?.role === 'employee' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="companySearch"
                      label="Search for Company (Name or Email)"
                      // value={values.companySearch}
                      {...getFieldProps('companySearch')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>
                )}
              </Grid>

              {data && data?.role === 'company' && (
                <>


                  <Typography variant="h6" sx={{ mt: 4 }}>
                    Company Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="companyName"
                        label="Company Name"
                        // value={values.companyName}
                        {...getFieldProps('companyName')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.companyName && Boolean(errors.companyName)}
                        helperText={touched.companyName && errors.companyName}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="companyEmailDomain"
                        label="Company Domain (optional)"
                        // value={values.companyEmailDomain}
                        {...getFieldProps('companyEmailDomain')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.companyEmailDomain && Boolean(errors.companyEmailDomain)}
                        helperText={touched.companyEmailDomain && errors.companyEmailDomain}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="companySize"
                        label="Company Size (optional)"
                        // value={values.companySize}
                        {...getFieldProps('companySize')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        name="industry"
                        label="Industry (optional)"
                        // value={values.industry}
                        {...getFieldProps('industry')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ mt: 4 }}
              >
                {t('Submit')}
              </Button>
            </Form>
          )}
        </Formik>
      </Paper>
    </Container >
  );
};

export default OnboardingSettings;
